import React, { useEffect, useRef, useState } from "react";
import { Col, Row, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import parse from "react-html-parser";
import "./BidDepositCharge.css";
import PageFooter from "@/components/common/PageFooter";
import LoadedText from "@/components/loader/LoadedText";
import LoadingText from "@/components/loader/LoadingText";
import { trackPageMount } from "@/utils/analytics";

import { validateForm } from "@/utils/formValidator";
import { formatCmsTextToHtml } from "@/utils/textParser";
import { bidDepositChargeDetails } from "@/api/bids";


const BidDepositCharge = ({ propertyId }) => {

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiSuccessMessage, setApiSuccessMessage] = useState("");
  const [blurredFields, setBlurredFields] = useState({
    firstName: false,
    lastName: false,
    emailId: false,
    phone: false,
    descriptionText: false,
    propertyId: false,
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    phone: "",
    descriptionText: "",
    propertyId: "",
    apiError: "",
  });
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    phone: "",
    descriptionText: "",
    propertyId: propertyId || "",
  });


  useEffect(() => {
    trackPageMount({
      pageName: "bid_deposit_charge_from_email",
      eventCategory: "Bid Deposit Charge from email",
      eventSource: "on page mount",
    });
  }, []);

  const validateDescription = (text) => {
    const dangerousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /<iframe[^>]*>.*?<\/iframe>/gi,
      /<object[^>]*>.*?<\/object>/gi,
      /<embed[^>]*>/gi,
      /<link[^>]*>/gi,
      /<meta[^>]*>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<[^>]*>/g
    ];

    return dangerousPatterns.some(pattern => pattern.test(text));
  };

 const handleBlur = (e) => {
  const { name, value } = e.target;
  setBlurredFields((prev) => ({
    ...prev,
    [name]: true,
  }));

  // Special validation for description
  if (name === "descriptionText" && value.trim()) {
    if (validateDescription(value)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "Description contains invalid characters or HTML tags",
      }));
      return;
    }
  }

  // Special validation for email since validateForm has a bug
  if (name === "emailId" && value.trim()) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "Invalid email address",
      }));
      return;
    }
  }

  // Run validation for other fields
  const validationErrors = validateForm({ [name]: value }, [name]);

  setErrors((prevErrors) => ({
    ...prevErrors,
    [name]: validationErrors[name] || "",
  }));
};


const handleInputChange = (e) => {
  const { name, value } = e.target;

  // Only allow numbers for phone field
  let filteredValue = value;
  if (name === "phone") {
    filteredValue = value.replace(/[^0-9]/g, "");
  }

  setFormData((prev) => ({
    ...prev,
    [name]: filteredValue,
  }));

  setErrors((prev) => ({
    ...prev,
    [name]: "",
  }));

  setBlurredFields((prev) => ({
    ...prev,
    [name]: false,
  }));
};


  const handleSubmit = async () => {
    try {
      // Check if there are any existing errors
      const hasExistingErrors = Object.values(errors).some(error => error && error !== "");
      if (hasExistingErrors) {
        toast.error("Please fix all errors before submitting");
        return;
      }

      const validationErrors = {};

      if (!formData.firstName.trim()) validationErrors.firstName = "First name is required";
      if (!formData.lastName.trim()) validationErrors.lastName = "Last name is required";
      if (!formData.emailId.trim()) validationErrors.emailId = "Email is required";
      if (!formData.phone.trim()) validationErrors.phone = "Phone is required";
      if (!formData.propertyId.trim()) validationErrors.propertyId = "Property ID is required";
      if (!formData.descriptionText.trim()) {
        validationErrors.descriptionText = "Description is required";
      } else if (validateDescription(formData.descriptionText)) {
        validationErrors.descriptionText = "Description contains invalid characters or HTML tags";
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsLoading(true);

      const response = await bidDepositChargeDetails(formData);

      if (response.status === "SUCCESS") {
        setApiSuccessMessage(response.successMessage || successMsg);
        setIsSuccess(true);
      } else {
        setErrors(prev => ({
          ...prev,
          apiError: response.errorMessage || "Failed to submit form"
        }));
      }

    } catch (error) {
      console.log("Failed to submit form");
      toast.error(error?.message || "Failed to submit form");
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div>
      {/* <!-- Main Header Nav --> */}

      <div className="container bid-deposit-container">
        {isSuccess ? (
          <Row className="mb-5">
            <Col>
              <div className="text-center" style={{ padding: "40px 20px" }}>
                <h4 style={{ color: "green", fontFamily: "graphikregular" }}>
                  {apiSuccessMessage}
                </h4>
              </div>
            </Col>
          </Row>
        ) : (
          <>
            <div className="mb-4 bid-deposit-text-container">
              <h3>Bid Deposit Charge Confirmation Dispute</h3>
            </div>
            <Row className="mb-3">
            <Col sm={3} className="lable-text">
              <label className="form-label" style={{ fontFamily: "graphikregular" }}>First Name <span style={{ color: "red" }}>*</span></label>
            </Col>
            <Col sm={9}>
              <input
                type="text"
                className="form-control"
                placeholder="First Name"
                value={formData.firstName}
                name="firstName"
                maxLength="40"
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.firstName && (
                <div className="text-danger" style={{ fontSize: "12px" }}>
                  {errors.firstName}
                </div>
              )}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3} className="lable-text">
              <label className="form-label lable-text" style={{ fontFamily: "graphikregular" }}>Last Name <span style={{ color: "red" }}>*</span></label>
            </Col>
            <Col sm={9}>
              <input
                type="text"
                className="form-control"
                placeholder="Last Name"
                value={formData.lastName}
                name="lastName"
                maxLength="40"
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.lastName && (
                <div className="text-danger" style={{ fontSize: "12px" }}>
                  {errors.lastName}
                </div>
              )}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3} className="lable-text">
              <label className="form-label lable-text" style={{ fontFamily: "graphikregular" }}>Email Address <span style={{ color: "red" }}>*</span></label>
            </Col>
            <Col sm={9}>
              <input
                type="email"
                className="form-control"
                placeholder="Email Address"
                value={formData.emailId}
                name="emailId"
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.emailId && (
                <div className="text-danger" style={{ fontSize: "12px" }}>
                  {errors.emailId}
                </div>
              )}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3} className="lable-text">
              <label className="form-label lable-text" style={{ fontFamily: "graphikregular" }}>Phone Number <span style={{ color: "red" }}>*</span></label>
            </Col>
            <Col sm={9}>
              <input
                type="tel"
                className="form-control"
                placeholder="Phone Number"
                value={formData.phone}
                name="phone"
                maxLength="10"
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.phone && (
                <div className="text-danger" style={{ fontSize: "12px" }}>
                  {errors.phone}
                </div>
              )}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3} className="lable-text">
              <label className="form-label lable-text" style={{ fontFamily: "graphikregular" }}>Property ID <span style={{ color: "red" }}>*</span></label>
            </Col>
            <Col sm={9}>
              <input
                type="text"
                className="form-control"
                placeholder="Property ID"
                value={formData.propertyId}
                name="propertyId"
                disabled
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.propertyId && (
                <div className="text-danger" style={{ fontSize: "12px" }}>
                  {errors.propertyId}
                </div>
              )}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3} className="lable-text">
              <label className="form-label lable-text" style={{ fontFamily: "graphikregular" }}>Description <span style={{ color: "red" }}>*</span></label>
            </Col>
            <Col sm={9}>
              <textarea
                className="form-control"
                placeholder="Enter reason for dispute"
                rows="4"
                value={formData.descriptionText}
                name="descriptionText"
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.descriptionText && (
                <div className="text-danger" style={{ fontSize: "12px" }}>
                  {errors.descriptionText}
                </div>
              )}
            </Col>
          </Row>

          <Row className="mb-5">
            <Col sm={3} className="lable-text"></Col>
            <Col sm={9}>
              <Button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <LoadingText text="Processing..." />
                ) : (
                  <LoadedText text="Submit" />
                )}
              </Button>
            </Col>
          </Row>

          {errors.apiError && (
            <Row className="mb-3">
              <Col>
                <div
                  className="text-danger"
                  style={{
                    fontSize: "12px",
                    fontFamily: "graphikregular",
                    width: "70%",
                  }}
                >
                  {parse(formatCmsTextToHtml(errors.apiError))}
                </div>
              </Col>
            </Row>
          )}
          </>
        )}
      </div>
      <PageFooter showPartners={false} />
    </div>
  );
};

export default BidDepositCharge;
